/**
 * Canvas Serialization Service
 * Handles saving, loading, and converting canvas workflows
 */

import { 
  SerializedCanvas, 
  SerializedNode, 
  SerializedConnection, 
  SerializedGroup,
  CanvasToNodeOptions,
  CanvasNodeDefinition,
  ValidationResult,
  ExportFormat,
  ImportExportOptions,
  CanvasMetadata,
  CanvasLayout,
  CanvasSettings
} from '../types/canvasSerialization';
import { NodeInstance } from '../types/nodeDefinition';
import { Connection } from '../types/socket';
import { WorkflowGroup } from '../types/workflowGroup';
import { v4 as uuidv4 } from 'uuid';

/**
 * Main Canvas Serialization Service
 */
export class CanvasSerializationService {
  private static readonly CURRENT_VERSION = '1.0.0';
  private static readonly SUPPORTED_VERSIONS = ['1.0.0'];

  /**
   * Serialize current canvas state to JSON
   */
  serializeCanvas(
    canvasId: string,
    name: string,
    nodes: NodeInstance[],
    connections: Connection[],
    groups: WorkflowGroup[],
    viewport?: { zoom: number; pan: { x: number; y: number } },
    options?: Partial<ImportExportOptions>
  ): SerializedCanvas {
    const now = new Date().toISOString();
    
    // Calculate canvas statistics
    const statistics = this.calculateCanvasStatistics(nodes, connections, groups);
    
    // Generate metadata
    const metadata: CanvasMetadata = {
      createdAt: now,
      updatedAt: now,
      tags: [],
      category: 'workflow',
      statistics,
      compatibility: {
        minimumVersion: CanvasSerializationService.CURRENT_VERSION,
        requiredExtensions: [],
        supportedFormats: Object.values(ExportFormat)
      }
    };

    // Serialize components
    const serializedNodes = nodes.map(node => this.serializeNode(node));
    const serializedConnections = connections.map(conn => this.serializeConnection(conn));
    const serializedGroups = groups.map(group => this.serializeGroup(group));

    // Generate layout information
    const layout: CanvasLayout = this.generateCanvasLayout(viewport);
    
    // Generate default settings
    const settings: CanvasSettings = this.generateDefaultSettings();

    const serializedCanvas: SerializedCanvas = {
      id: canvasId,
      name,
      description: `Canvas with ${statistics.nodeCount} nodes`,
      version: CanvasSerializationService.CURRENT_VERSION,
      metadata,
      nodes: serializedNodes,
      connections: serializedConnections,
      groups: serializedGroups,
      layout,
      settings
    };

    return serializedCanvas;
  }

  /**
   * Deserialize canvas from JSON
   */
  deserializeCanvas(
    serializedCanvas: SerializedCanvas,
    options?: Partial<ImportExportOptions>
  ): {
    nodes: NodeInstance[];
    connections: Connection[];
    groups: WorkflowGroup[];
    metadata: CanvasMetadata;
    validationResults: ValidationResult[];
  } {
    // Validate version compatibility
    if (!CanvasSerializationService.SUPPORTED_VERSIONS.includes(serializedCanvas.version)) {
      throw new Error(`Unsupported canvas version: ${serializedCanvas.version}`);
    }

    const validationResults: ValidationResult[] = [];

    // Deserialize components
    const nodes = serializedCanvas.nodes.map(serializedNode => {
      try {
        return this.deserializeNode(serializedNode);
      } catch (error) {
        validationResults.push({
          type: 'error',
          message: `Failed to deserialize node ${serializedNode.id}: ${error}`,
          nodeId: serializedNode.id,
          severity: 'critical',
          autoFixable: false
        });
        return null;
      }
    }).filter(Boolean) as NodeInstance[];

    const connections = serializedCanvas.connections.map(serializedConn => {
      try {
        return this.deserializeConnection(serializedConn);
      } catch (error) {
        validationResults.push({
          type: 'error',
          message: `Failed to deserialize connection ${serializedConn.id}: ${error}`,
          connectionId: serializedConn.id,
          severity: 'high',
          autoFixable: false
        });
        return null;
      }
    }).filter(Boolean) as Connection[];

    const groups = serializedCanvas.groups.map(serializedGroup => {
      try {
        return this.deserializeGroup(serializedGroup);
      } catch (error) {
        validationResults.push({
          type: 'warning',
          message: `Failed to deserialize group ${serializedGroup.id}: ${error}`,
          severity: 'medium',
          autoFixable: true
        });
        return null;
      }
    }).filter(Boolean) as WorkflowGroup[];

    return {
      nodes,
      connections,
      groups,
      metadata: serializedCanvas.metadata,
      validationResults
    };
  }

  /**
   * Convert canvas to reusable node definition
   */
  convertCanvasToNode(
    serializedCanvas: SerializedCanvas,
    options: CanvasToNodeOptions
  ): CanvasNodeDefinition {
    const nodeId = `canvas-node-${uuidv4()}`;
    
    // Generate interface from canvas boundaries
    const canvasInterface = this.generateCanvasInterface(serializedCanvas, options);
    
    // Validate canvas for conversion
    const validationResults = this.validateCanvasForConversion(serializedCanvas);
    
    // Calculate dependencies
    const dependencies = this.extractCanvasDependencies(serializedCanvas);
    
    const canvasNodeDefinition: CanvasNodeDefinition = {
      id: nodeId,
      type: 'canvas-node',
      name: serializedCanvas.name,
      description: serializedCanvas.description || `Composite node from ${serializedCanvas.name}`,
      category: 'composite',
      serializedCanvas: options.includeMetadata ? serializedCanvas : this.stripMetadata(serializedCanvas),
      interface: canvasInterface,
      thumbnail: serializedCanvas.metadata.thumbnail,
      version: serializedCanvas.version,
      metadata: {
        originalCanvasId: serializedCanvas.id,
        conversionDate: new Date().toISOString(),
        sourceVersion: serializedCanvas.version,
        validationResults,
        dependencies
      }
    };

    return canvasNodeDefinition;
  }

  /**
   * Export canvas to various formats
   */
  exportCanvas(
    serializedCanvas: SerializedCanvas,
    format: ExportFormat,
    options?: Partial<ImportExportOptions>
  ): string | Uint8Array {
    switch (format) {
      case ExportFormat.JSON:
        return JSON.stringify(serializedCanvas, null, 2);
      
      case ExportFormat.YAML:
        // Note: In a real implementation, you'd use a YAML library
        return `# YAML export not implemented\n# ${JSON.stringify(serializedCanvas)}`;
      
      case ExportFormat.XML:
        return this.convertToXML(serializedCanvas);
      
      case ExportFormat.BINARY:
        return this.convertToBinary(serializedCanvas);
      
      case ExportFormat.COMPRESSED:
        return this.compressCanvas(serializedCanvas);
      
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  // Private helper methods

  private serializeNode(node: NodeInstance): SerializedNode {
    return {
      id: node.id,
      type: node.definition.id,
      position: { x: node.x, y: node.y },
      size: { width: node.width || 200, height: node.height || 120 },
      properties: node.properties || {},
      settings: {
        viewMode: node.viewMode || 'compact',
        collapsed: false,
        visible: true,
        locked: false
      },
      metadata: {
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        executionCount: 0
      }
    };
  }

  private serializeConnection(connection: Connection): SerializedConnection {
    return {
      id: connection.id,
      sourceNodeId: connection.sourceNodeId,
      sourceSocketId: connection.sourceSocketId,
      targetNodeId: connection.targetNodeId,
      targetSocketId: connection.targetSocketId,
      type: connection.type || 'data',
      metadata: {
        createdAt: new Date().toISOString(),
        dataFlowDirection: 'forward',
        validationStatus: 'valid'
      }
    };
  }

  private serializeGroup(group: WorkflowGroup): SerializedGroup {
    return {
      id: group.id,
      name: group.name,
      description: group.description,
      nodeIds: group.nodeIds,
      position: group.position,
      size: group.size,
      color: group.color,
      collapsed: group.collapsed,
      metadata: {
        createdAt: group.metadata.createdAt,
        updatedAt: group.metadata.updatedAt,
        version: group.metadata.version,
        isTemplate: false
      },
      interface: group.interface
    };
  }

  private deserializeNode(serializedNode: SerializedNode): NodeInstance {
    // This is a simplified version - in reality, you'd need to reconstruct
    // the full NodeInstance with proper definition lookup
    const nodeInstance: NodeInstance = {
      id: serializedNode.id,
      definition: {
        id: serializedNode.type,
        name: serializedNode.type,
        category: 'unknown',
        inputs: [],
        outputs: [],
        properties: []
      },
      x: serializedNode.position.x,
      y: serializedNode.position.y,
      width: serializedNode.size?.width,
      height: serializedNode.size?.height,
      properties: serializedNode.properties,
      viewMode: serializedNode.settings.viewMode
    };

    return nodeInstance;
  }

  private deserializeConnection(serializedConnection: SerializedConnection): Connection {
    return {
      id: serializedConnection.id,
      sourceNodeId: serializedConnection.sourceNodeId,
      sourceSocketId: serializedConnection.sourceSocketId,
      targetNodeId: serializedConnection.targetNodeId,
      targetSocketId: serializedConnection.targetSocketId,
      type: serializedConnection.type
    };
  }

  private deserializeGroup(serializedGroup: SerializedGroup): WorkflowGroup {
    return {
      id: serializedGroup.id,
      name: serializedGroup.name,
      description: serializedGroup.description,
      nodeIds: serializedGroup.nodeIds,
      position: serializedGroup.position,
      size: serializedGroup.size,
      color: serializedGroup.color,
      collapsed: serializedGroup.collapsed,
      metadata: {
        createdAt: serializedGroup.metadata.createdAt,
        updatedAt: serializedGroup.metadata.updatedAt,
        version: serializedGroup.metadata.version,
        tags: []
      },
      interface: serializedGroup.interface
    };
  }

  private calculateCanvasStatistics(
    nodes: NodeInstance[], 
    connections: Connection[], 
    groups: WorkflowGroup[]
  ) {
    const nodeCount = nodes.length;
    const connectionCount = connections.length;
    const groupCount = groups.length;
    
    let complexity: 'simple' | 'medium' | 'complex' | 'advanced' = 'simple';
    if (nodeCount > 20 || connectionCount > 30) complexity = 'advanced';
    else if (nodeCount > 10 || connectionCount > 15) complexity = 'complex';
    else if (nodeCount > 5 || connectionCount > 8) complexity = 'medium';

    return {
      nodeCount,
      connectionCount,
      groupCount,
      complexity
    };
  }

  private generateCanvasLayout(viewport?: { zoom: number; pan: { x: number; y: number } }): CanvasLayout {
    return {
      viewport: {
        zoom: viewport?.zoom || 1,
        pan: viewport?.pan || { x: 0, y: 0 },
        center: { x: 0, y: 0 },
        bounds: { minX: -5000, minY: -5000, maxX: 5000, maxY: 5000 }
      },
      grid: {
        enabled: true,
        size: 20,
        snapToGrid: false,
        showGrid: true,
        color: '#374151',
        style: 'dots'
      },
      theme: {
        name: 'default-dark',
        colors: {
          background: '#111827',
          canvas: '#0f172a',
          nodes: {
            default: '#374151',
            selected: '#3b82f6',
            hover: '#4b5563',
            border: '#6b7280'
          },
          connections: {
            default: '#8b5cf6',
            active: '#a855f7',
            error: '#ef4444'
          },
          ui: {
            primary: '#3b82f6',
            secondary: '#6b7280',
            accent: '#8b5cf6',
            text: '#ffffff',
            textSecondary: '#d1d5db'
          }
        },
        fonts: {
          primary: 'Inter, sans-serif',
          secondary: 'Inter, sans-serif',
          monospace: 'Monaco, monospace',
          sizes: { small: 12, medium: 14, large: 16, xlarge: 20 }
        },
        spacing: { small: 8, medium: 16, large: 24, xlarge: 32 }
      },
      ui: {
        showMinimap: false,
        showNodePreview: true,
        showConnectionLabels: false,
        showExecutionIndicators: true,
        animationSpeed: 'normal',
        tooltipDelay: 500
      }
    };
  }

  private generateDefaultSettings(): CanvasSettings {
    return {
      execution: {
        autoExecute: false,
        executionMode: 'sequential',
        maxConcurrentNodes: 4,
        timeout: 30000,
        retryAttempts: 3,
        cacheResults: true
      },
      validation: {
        validateOnEdit: true,
        validateOnExecute: true,
        strictTypeChecking: true,
        warnOnMissingConnections: true,
        errorHandling: 'lenient'
      },
      performance: {
        renderingMode: 'optimized',
        maxNodesInView: 100,
        virtualizeOffscreen: true,
        updateThrottling: 16,
        memoryLimit: 512
      },
      debugging: {
        enableDebugMode: false,
        logLevel: 'warn',
        showExecutionTrace: false,
        recordExecutionHistory: false,
        maxHistoryEntries: 100
      }
    };
  }

  private generateCanvasInterface(
    serializedCanvas: SerializedCanvas,
    options: CanvasToNodeOptions
  ) {
    // Simplified interface generation - analyze canvas boundaries
    const inputs = [];
    const outputs = [];
    const properties = [];

    // TODO: In a full implementation, analyze connections to determine interface
    
    return { inputs, outputs, properties };
  }

  private validateCanvasForConversion(serializedCanvas: SerializedCanvas): ValidationResult[] {
    const results: ValidationResult[] = [];
    
    // Basic validation
    if (serializedCanvas.nodes.length === 0) {
      results.push({
        type: 'error',
        message: 'Canvas has no nodes',
        severity: 'critical',
        autoFixable: false
      });
    }

    return results;
  }

  private extractCanvasDependencies(serializedCanvas: SerializedCanvas) {
    // Extract unique node types as dependencies
    const nodeTypes = [...new Set(serializedCanvas.nodes.map(n => n.type))];
    
    return nodeTypes.map(type => ({
      type: 'node-type' as const,
      name: type,
      required: true
    }));
  }

  private stripMetadata(serializedCanvas: SerializedCanvas): SerializedCanvas {
    // Create a copy without heavy metadata
    return {
      ...serializedCanvas,
      metadata: {
        ...serializedCanvas.metadata,
        thumbnail: undefined
      }
    };
  }

  private convertToXML(serializedCanvas: SerializedCanvas): string {
    // Simplified XML conversion
    return `<?xml version="1.0" encoding="UTF-8"?>
<canvas id="${serializedCanvas.id}" name="${serializedCanvas.name}">
  <!-- XML conversion not fully implemented -->
  <!-- Use JSON format for full functionality -->
</canvas>`;
  }

  private convertToBinary(serializedCanvas: SerializedCanvas): Uint8Array {
    // Simplified binary conversion using JSON + UTF-8 encoding
    const jsonString = JSON.stringify(serializedCanvas);
    return new TextEncoder().encode(jsonString);
  }

  private compressCanvas(serializedCanvas: SerializedCanvas): string {
    // Simplified compression - in reality, you'd use a compression library
    const jsonString = JSON.stringify(serializedCanvas);
    return btoa(jsonString); // Base64 "compression"
  }
}

// Singleton instance
export const canvasSerializationService = new CanvasSerializationService();